/**
 * The jump palette's matching, ordering and page list, kept free of React so
 * they can be tested on their own.
 */
import { routes } from "@/lib/jobs";
import { TOKEN_SECTIONS } from "@/lib/sections";

/** What the palette ranks: a command without its action. */
export interface Entry {
  id: string;
  label: string;
  hint?: string;
  group: string;
  keywords?: string[];
}

/** How well one query word matches one haystack word: 3 prefix, 2 inside, 1 in order, 0 not at all. */
function wordScore(query: string, word: string): number {
  if (word.startsWith(query)) return 3;
  if (word.includes(query)) return 2;
  let at = 0;
  for (const ch of word) if (ch === query[at]) at++;
  return at === query.length ? 1 : 0;
}

/** Every query word must match some word of the entry; the score adds up the best match per query word. */
export function score(entry: Entry, query: string): number {
  const words = [entry.label, entry.hint ?? "", entry.group, ...(entry.keywords ?? [])]
    .join(" ")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
  let total = 0;
  for (const q of query.toLowerCase().split(/\s+/).filter(Boolean)) {
    const best = Math.max(0, ...words.map((w) => wordScore(q, w)));
    if (best === 0) return 0;
    total += best;
  }
  return total;
}

/**
 * The entries to show for a query, best first. With no query: the recent
 * jumps (most recent first), then everything else in its given order. With a
 * query: matches by score, a recent jump ahead of an equal match, then the
 * given order.
 */
export function rank<T extends Entry>(
  entries: readonly T[],
  query: string,
  recent: readonly string[],
  limit = 50,
): T[] {
  const recency = (id: string) => {
    const i = recent.indexOf(id);
    return i === -1 ? Infinity : i;
  };
  const scored = entries
    .map((e, order) => ({ e, order, s: query.trim() ? score(e, query) : 1, r: recency(e.id) }))
    .filter((x) => x.s > 0);
  scored.sort((a, b) => b.s - a.s || a.r - b.r || a.order - b.order);
  return scored.slice(0, limit).map((x) => x.e);
}

/** `recent` with `id` moved to the front, at most `max` long. */
export function pushRecent(recent: readonly string[], id: string, max = 8): string[] {
  return [id, ...recent.filter((r) => r !== id)].slice(0, max);
}

export interface PageEntry extends Entry {
  route: string;
}

interface Jobs {
  tokens: { id: string; chain: string; token: string; symbol: string }[];
  liquidity: { chain: string; available?: boolean }[];
  hooks: { chain: string; available?: boolean }[];
}

/** Every page the palette can jump to: each token × each lib/sections page, then the chain and global pages. */
export function pageEntries(jobs: Jobs | undefined): PageEntry[] {
  const out: PageEntry[] = [
    { id: "page:home", label: "all tokens", group: "pages", route: routes.home(), keywords: ["home"] },
  ];
  for (const t of jobs?.tokens ?? []) {
    for (const s of TOKEN_SECTIONS) {
      out.push({
        id: `page:${t.id}/${s.id}`,
        label: `${t.symbol} · ${s.label}`,
        hint: `${t.chain} · ${t.token.slice(-4)}`,
        group: "tokens",
        keywords: s.keywords,
        route: s.path(t.chain, t.token),
      });
    }
  }
  for (const j of jobs?.liquidity ?? [])
    out.push({
      id: `page:liquidity:${j.chain}`,
      label: `liquidity ledgers · ${j.chain}`,
      group: "chains",
      keywords: ["lp", "ledger"],
      route: routes.ledgers(j.chain),
    });
  for (const j of (jobs?.hooks ?? []).filter((h) => h.available !== false))
    out.push({
      id: `page:hooks:${j.chain}`,
      label: `hooks · ${j.chain}`,
      group: "chains",
      route: routes.hooks(j.chain),
    });
  out.push(
    { id: "page:signals", label: "signals", group: "pages", route: routes.signals() },
    {
      id: "page:alerts",
      label: "alerts",
      group: "pages",
      route: routes.alerts(),
      keywords: ["notifications", "rules"],
    },
    {
      id: "page:positions",
      label: "positions",
      group: "pages",
      route: routes.positions(),
      keywords: ["mine", "portfolio", "pnl"],
    },
    {
      id: "page:health",
      label: "health",
      group: "pages",
      route: routes.health(),
      keywords: ["status", "sync"],
    },
    { id: "page:compare", label: "compare", group: "pages", route: routes.compare() },
  );
  return out;
}
